import React, { forwardRef } from "react";

import {
  Alert as PrimitiveAlert,
  AlertTitle,
  AlertDescription,
} from "src/primitives/Alert";
import { cn } from "src/shadcn/lib/utils";
import { renderIcon, type IconProp } from "src/shared/renderIcon";

import { VARIANT_CONFIG } from "./constants";

export type CalloutVariant = "info" | "warning" | "danger" | "success";

type PrimitiveAlertProps = React.ComponentProps<typeof PrimitiveAlert>;

export interface CalloutProps extends Omit<PrimitiveAlertProps, "variant"> {
  /** Visual style of the callout. */
  variant?: CalloutVariant;
  /** Custom icon. Defaults to a variant-specific icon. */
  icon?: IconProp;
  /** Optional title rendered as AlertTitle. */
  title?: string;
  /** Content rendered as AlertDescription. */
  children?: React.ReactNode;
}

const Callout = forwardRef<HTMLDivElement, CalloutProps>(
  (
    { variant = "info", icon, title, children, className, ...otherProps },
    ref
  ) => {
    const config = VARIANT_CONFIG[variant];
    const resolvedIcon = icon ?? config.icon;

    return (
      <PrimitiveAlert
        ref={ref}
        className={cn(config.container, className)}
        {...otherProps}
      >
        {renderIcon(resolvedIcon, "size-4 shrink-0")}
        {title && <AlertTitle className="font-semibold">{title}</AlertTitle>}
        {children && (
          <AlertDescription className="text-foreground">
            {children}
          </AlertDescription>
        )}
      </PrimitiveAlert>
    );
  }
);

Callout.displayName = "Callout";

export { Callout };
